import {
    User,
    userApi,
    UserAdminEditModel,
    UserId,
    UserSearchResults,
    UserSearchRequest,
} from "@/api/users";
import { USER_KEYS } from "@/queries/users";
import { useQueryCache } from "@pinia/colada";
import { defineStore } from "pinia";

export type UsersState = {
    search: {
        searched: boolean;
        results: UserSearchResults[];
        total: number;
        request: UserSearchRequest;
        options: UserSearchOptions;
    };
};

export type UserSearchOptions = {
    page: number | null;
    perPage: number | null;
};

// The user list, avatars, and profiles live in queries/users.ts; this store
// keeps the admin search and the mutations, which write through the cache.
export const useUsersStore = defineStore("users", {
    state: (): UsersState => ({
        search: {
            searched: false,
            results: [],
            total: 0,
            request: {
                anyNameField: null,
                userName: null,
                displayName: null,
                email: null,
                organizationId: null,
                projectId: null,
                isDisabled: null,
                order: null,
                direction: null,
            },
            options: {
                page: null,
                perPage: null,
            },
        },
    }),
    actions: {
        async doSearch(
            request: UserSearchRequest,
            page: number | null,
            perPage: number | null,
            storeRequest = true
        ) {
            // Fetch the data from the server
            const result = await userApi.search(request, page, perPage);

            // Update the state with the new data
            this.$state.search.searched = true;
            this.$state.search.results = result.users;
            this.$state.search.total = result.total;

            this.$state.search.options.page = page;
            this.$state.search.options.perPage = perPage;

            if (storeRequest) {
                this.$state.search.request = request;
            }
        },
        async loadActivity(id: UserId, page: number, perPage: number, day: string | null = null) {
            return await userApi.getActivity(id, page, perPage, day);
        },
        async invalidateUsers() {
            await useQueryCache().invalidateQueries({ key: USER_KEYS.list() });
        },
        async createUser(email: string, displayName: string | null, userName: string | null) {
            await userApi.createUser(email, displayName, userName);
            await this.invalidateUsers();
        },
        async adminEditUser(id: UserId, model: UserAdminEditModel) {
            const user = await userApi.adminEditUser(id, model);
            await this.invalidateUsers();

            const result = this.$state.search.results.find((r) => r.id === id);
            if (result) {
                result.email = user.email;
                result.displayName = user.displayName;
            }
        },
        modifyUser(user: User) {
            useQueryCache().setQueryData<User[]>(USER_KEYS.list(), (users) =>
                (users ?? []).map((u) => (u.id === user.id ? user : u))
            );
        },
        async mergeUsers(sourceId: UserId, targetId: UserId): Promise<string> {
            return await userApi.mergeUser(sourceId, targetId);
        },
        async reloadAfterMerge(sourceId: UserId) {
            // The merge deletes the source account; drop it locally and refresh from the server.
            await this.invalidateUsers();
            this.$state.search.results = this.$state.search.results.filter(
                (r) => r.id !== sourceId
            );
            this.$state.search.total = Math.max(0, this.$state.search.total - 1);
        },
        async setUserDisabled(id: UserId, isDisabled: boolean) {
            await userApi.setUserDisabled(id, isDisabled);
            await this.invalidateUsers();

            const result = this.$state.search.results.find((r) => r.id === id);
            if (result) {
                result.isDisabled = isDisabled;
            }
        },
        async resetUserTwoFactor(id: UserId) {
            await userApi.resetTwoFactor(id);

            const result = this.$state.search.results.find((r) => r.id === id);
            if (result) {
                result.twoFactorEnabled = false;
            }
        },
        async setUserAdmin(id: UserId, isAdmin: boolean) {
            await userApi.setUserAdmin(id, isAdmin);

            const result = this.$state.search.results.find((r) => r.id === id);
            if (result) {
                result.isAdmin = isAdmin;
            }
        },
    },
});
